import {
  AutocompleteFreeSoloValueMapping,
  AutocompleteProps,
  AutocompleteRenderGetTagProps,
  Checkbox,
  InputProps,
} from '@mui/material';
import React, { ChangeEvent, Fragment, forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { InputSize } from '../../model';
import { INPUT_BASE_PADDING_Y_PX } from '../../theme/component-overrides/input-base';
import { SxProps } from '../../utils';
import { VirtualizedAutocomplete } from '../../utils/VirtualizedAutocomplete';
import { Chip } from '../Chip';
import { MenuItem } from '../Menu';
import { SelectOption } from '../Select';
import { Stack } from '../Stack';
import { TextField } from '../TextField';
import { Tooltip } from '../Tooltip';
import { Typography } from '../Typography';
import { sortOptions } from './utils';

export interface MultiSelectProps<FreeSolo extends boolean | undefined> {
  /**
   * List of options shown in the select.
   */
  options: SelectOption[];
  /**
   * If true, the component is disabled.
   */
  disabled?: boolean;
  /**
   * If `true`, the `input` will indicate an error.
   * The prop defaults to the value (`false`) inherited from the parent FormControl component.
   */
  error?: boolean;
  /**
   * A function that determines the filtered options to be rendered on search.
   *
   * @param {T[]} options The options to render.
   * @param {object} state The state of the component.
   * @returns {T[]}
   */
  filterOptions?: AutocompleteProps<SelectOption, true, true, true, typeof Chip>['filterOptions'];
  /**
   * If true, the Autocomplete is free solo, meaning that the user input is not bound to provided options.
   * @default false
   */
  freeSolo?: FreeSolo;
  /**
   * Used to determine the string value for a given option. It's used to fill the input.
   */
  getOptionLabel?: (option: SelectOption) => string;
  /**
   * Text that appears below the field for when an validation error occurs or additional context is required.
   */
  helperText?: string;
  /**
   * The id of the component
   */
  id?: string;
  /**
   * Props applied to the Input element.
   */
  InputProps?: Partial<InputProps>;
  /**
   * If true, the component is in a loading state.
   */
  isLoading?: boolean;
  /**
   * Used to determine if the option represents the given value. Uses strict equality by default.
   */
  isOptionEqualToValue?: (option: SelectOption, value: SelectOption) => boolean;
  /**
   * Label of the input
   */
  label?: React.ReactNode;
  /**
   * Height for rendering the Listbox of options. Any CSS string value for the `height` property is valid (e.g. `100px`,
   * `3em`, etc.)
   */
  listboxHeight?: string;
  /**
   * Limits the number of selections in the dropdown that are visible when the input is not focused
   * https://mui.com/material-ui/react-autocomplete/#limit-tags
   */
  maxChipsDisplayed?: number;
  /**
   * Callback fired when an option is defocused. Returns all selected values.
   */
  onBlur?: React.FocusEventHandler<HTMLDivElement> | undefined;
  /**
   * Callback fired when an option is selected. Returns all selected values.
   */
  onChange?: (e: React.SyntheticEvent, values: SelectOption[]) => void;
  /**
   * Callback fired when the component requests to be closed.
   * Use in controlled mode (see open).
   *
   * @param {object} event The event source of the callback.
   */
  onClose?: (event: React.SyntheticEvent) => void;
  /**
   * Callback fired when the component requests to be opened.
   * Use in controlled mode (see open).
   *
   * @param {object} event The event source of the callback.
   */
  onOpen?: (event: React.SyntheticEvent) => void;
  /**
   * If true, the component is shown.
   */
  open?: boolean;
  /**
   * Width for the popup menu. When not set, the popup will match the width of the input.
   *
   * Useful in cases where the select is made very small to conserve space, but you do not want
   * to unnecessarily truncate the width of the menu items when opened.
   */
  popperWidth?: string;
  /**
   * The size of the component.
   * @default medium
   */
  size?: InputSize;
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps;
  /**
   * The value(s) to populate the list of selected options for the component.
   * If values is not passed in or passed as undefined, the component will stay in an uncontrolled state.
   * If values are passed in, use in controlled mode with the onChange prop.
   * https://mui.com/material-ui/react-autocomplete/#controlled-states
   */
  values?: SelectOption[] | undefined;
  /**
   * Whether to use the `chip` or `list` variant.
   * In the `chip` variant, selections appear as removable Chips.
   * In the `list` variant, selections appear in a comma-separated list.
   * @default 'chip'
   */
  variant?: 'chip' | 'list';
}

/**
 * @deprecated This component is deprecated. Please use the ComboboxSelect component instead.
 */
export const MultiSelect = forwardRef(function MultiSelect<T, FreeSolo extends boolean | undefined>(
  {
    label,
    options,
    values,
    onChange,
    InputProps,
    disabled,
    open,
    isLoading,
    getOptionLabel,
    error,
    maxChipsDisplayed,
    size = 'medium',
    onBlur,
    helperText,
    isOptionEqualToValue,
    variant = 'chip',
    popperWidth,
    ...props
  }: MultiSelectProps<FreeSolo>,
  ref: React.ForwardedRef<T>
) {
  const [selectedOptions, setSelectedOptions] = useState<SelectOption[]>(values ?? []);
  const [sortedOptions, setSortedOptions] = useState<SelectOption[]>(options);
  const [showTooltip, setShowTooltip] = useState(false);
  const [selectedOptionsWidth, setSelectedOptionsWidth] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const { onChange: inputPropsOnChange } = InputProps ?? {};

  /*
    If there is an InputProps.onChange function, we need to call both that and setInputValue
    when the input value changes. 
  */
  const handleInputValueChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setInputValue(value);
      if (inputPropsOnChange) {
        inputPropsOnChange(event);
      }
    },
    [inputPropsOnChange, setInputValue]
  );

  useEffect(() => {
    setSortedOptions(
      sortOptions({
        selectedOptions: values ?? selectedOptions,
        options,
      })
    );
    // Sort the options if they change. Hook does not need to run when selectedOptions, values or variant change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  const selectedOptionsListRef = useRef<HTMLParagraphElement>(null);
  const selectedChipRef = useRef<HTMLDivElement[]>([]);
  useEffect(() => {
    if (selectedOptionsListRef.current) {
      // Only show the tooltip when the selected options are truncated
      setShowTooltip(selectedOptionsListRef.current.scrollWidth > selectedOptionsListRef.current.clientWidth);
      setSelectedOptionsWidth(selectedOptionsListRef.current.clientWidth);
    }

    const selectedChipsWidth = selectedChipRef.current.reduce((totalWidth, item) => {
      return item.offsetWidth !== 0 ? totalWidth + item.offsetWidth + 4 : totalWidth;
    }, 0);
    if (selectedChipsWidth !== 0) {
      setSelectedOptionsWidth(selectedChipsWidth);
    }
  }, [isFocused, selectedOptions]);

  const renderTagsAsChips = (
    tags: Array<SelectOption | AutocompleteFreeSoloValueMapping<FreeSolo>>,
    getTagProps: AutocompleteRenderGetTagProps
  ) => {
    return tags.map((value, index) => {
      const formattedValue = typeof value === 'string' ? { key: value, label: value } : value;
      return (
        <Chip
          {...getTagProps({ index })}
          key={formattedValue.key}
          label={getOptionLabel ? getOptionLabel(formattedValue) : formattedValue.label}
          sx={{ maxWidth: '100% !important' }}
          ref={(el) => {
            if (el) {
              selectedChipRef.current[index] = el;
            }
          }}
        />
      );
    });
  };

  const renderTagsAsList = (tags: SelectOption[]) => {
    const selectedOptionsList = (
      <Typography
        sx={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        ref={selectedOptionsListRef}
      >
        {tags.map((tag, index) => (
          <Fragment key={index}>
            {getOptionLabel ? getOptionLabel(tag) : tag.label}
            {index !== tags.length - 1 && ', '}
          </Fragment>
        ))}
      </Typography>
    );
    return showTooltip ? (
      <Tooltip
        Content={
          <>
            {tags.map((tag, index) => (
              <Typography variant="body2" key={index}>
                {getOptionLabel ? getOptionLabel(tag) : tag.label}
              </Typography>
            ))}
          </>
        }
      >
        {selectedOptionsList}
      </Tooltip>
    ) : (
      selectedOptionsList
    );
  };

  return (
    <VirtualizedAutocomplete<SelectOption, true, undefined, FreeSolo>
      ref={ref}
      {...props}
      multiple
      limitTags={maxChipsDisplayed}
      autoHighlight
      disableCloseOnSelect
      disabled={disabled}
      options={sortedOptions}
      open={open}
      loading={isLoading}
      onBlur={(e) => {
        onBlur?.(e);
        setInputValue('');
        setIsFocused(false);
        setSortedOptions(
          sortOptions({
            selectedOptions: values ?? selectedOptions,
            options,
          })
        );
      }}
      onChange={(e, selectedValues) => {
        const formattedSelectedValues = selectedValues.map((value) => {
          // The value can be a regular string if free solo is enabled
          if (typeof value === 'string') {
            return {
              key: value,
              label: value,
            };
          } else {
            return value;
          }
        });
        onChange?.(e, formattedSelectedValues);
        setSelectedOptions(formattedSelectedValues);
      }}
      onFocus={() => setIsFocused(true)}
      openOnFocus
      inputValue={inputValue}
      value={values}
      isOptionEqualToValue={
        isOptionEqualToValue ??
        ((option, value) => {
          return typeof value === 'string'
            ? value === option.label
            : option.key === value.key && option.label === value.label;
        })
      }
      componentsProps={{
        popper: popperWidth
          ? {
              placement: 'bottom-start',
              style: {
                width: popperWidth,
              },
            }
          : undefined,
      }}
      renderTags={(tags, getTagProps) => {
        if (isFocused) {
          return null;
        } else {
          return variant === 'chip' ? renderTagsAsChips(tags, getTagProps) : renderTagsAsList(tags);
        }
      }}
      renderOption={(props, option) => {
        const selected = props['aria-selected'];
        return (
          <MenuItem {...props} key={option.key} description={option.description}>
            <Stack direction="row" spacing={1}>
              {<Checkbox sx={{ p: 0 }} size="small" checked={selected === true} />}
              <Typography sx={{ textWrap: 'wrap', wordBreak: 'break-all' }}>{option.label}</Typography>
            </Stack>
          </MenuItem>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          ref={InputProps?.ref ?? params.InputProps.ref}
          InputProps={{
            ...params.InputProps,
            sx: InputProps?.sx,
          }}
          label={label}
          error={error}
          size={size}
          helperText={helperText}
          onChange={handleInputValueChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setInputValue('');
            }
          }}
          sx={{
            ...(variant === 'chip'
              ? {
                  '.MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiChip-sizeSmall': {
                    marginY: 0,
                  },

                  '.MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
                    // Set the width of the input to the width of the selected items
                    // plus the 30px (to account for the space between the chips and
                    // the 'x' button). This ensures that the width of the input does
                    // not change when we hide the selected items on focus.
                    width: isFocused ? `${selectedOptionsWidth + 30}px` : 0,
                    paddingX: 0,
                  },
                }
              : {
                  '.MuiInputBase-root': {
                    flexWrap: 'nowrap',
                  },

                  '.MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
                    paddingY: `${INPUT_BASE_PADDING_Y_PX.small}px !important`,
                  },

                  '.MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input': {
                    padding: 0,
                  },

                  '.MuiOutlinedInput-root.MuiInputBase-adornedStart': {
                    paddingY: `${INPUT_BASE_PADDING_Y_PX.medium}px !important`,
                  },

                  '.MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
                    minWidth: 0,
                    // Set the width of the input to the width of the selected items.
                    // This ensures that the width of the input does not change when
                    // we hide the selected items on focus.
                    width: isFocused ? `${selectedOptionsWidth}px` : 0,
                    paddingX: 0,
                  },
                }),
          }}
        />
      )}
    />
  );
});
